<template>
    <section class="relative overflow-hidden">
        <div class="container">
            <AtomTitle class="md:text-center">
                <span v-if="isCromi()">
                    После продажи остаемся на&nbsp;связи 24/7, решаем вопросы<br
                        class="max-xl:hidden" />в течение 10-15 минут
                </span>
                <span v-else> Мощная техническая инженерная база </span>
            </AtomTitle>
            <div
                ref="videosContainer"
                class="relative mt-10 flex w-full flex-col items-stretch justify-center gap-5 md:flex-row">
                <picture
                    class="absolute top-0 z-10 h-auto w-[16rem] rotate-[-25deg] max-lg:hidden lg:right-[95%] xl:right-[82%]">
                    <source
                        type="image/webp"
                        srcset="
                            /assets/images/ui/icons/webp/person-wrench.webp
                        " />

                    <img
                        loading="lazy"
                        alt="person wrench"
                        src="/assets/images/ui/icons/jpg-png/person-wrench.png" />
                </picture>
                <div
                    v-for="(item, index) in WARRANTY_ITEMS_BY_DIRECTION[
                        props.directionSlug
                    ] || []"
                    :key="item.title"
                    ref="videoCards"
                    class="relative z-20 w-full max-w-[27.5rem] overflow-hidden rounded-12 bg-[#FFFFFF0D] p-3 transition-colors duration-500 lg:backdrop-blur-md">
                    <div
                        class="aspect-video shrink-0 overflow-hidden rounded-8">
                        <div
                            v-if="item.video"
                            class="relative h-full">
                            <video
                                aria-hidden="true"
                                tabindex="-1"
                                playsinline
                                preload="none"
                                pip="false"
                                muted
                                :src="item.video"
                                :data-order="index"
                                class="size-full object-cover"
                                @ended="handleVideoEnded" />
                        </div>
                    </div>
                    <div>
                        <h3
                            v-html="item.title"
                            class="mt-5 line-clamp-3 text-20/120 font-semibold text-ash-200 md:text-24/133" />
                        <div
                            v-html="item.description"
                            class="mt-3 text-16/150 font-medium text-accent-100 max-xl:line-clamp-4 md:mt-4" />
                    </div>
                </div>

                <picture
                    class="absolute top-[35%] z-10 h-auto w-[12.5rem] rotate-[15deg] max-lg:hidden lg:right-[-9%] xl:right-[3%]">
                    <source
                        type="image/webp"
                        srcset="
                            /assets/images/ui/icons/webp/person-assistant.webp
                        " />

                    <img
                        loading="lazy"
                        alt="person assistant"
                        src="/assets/images/ui/icons/jpg-png/person-assistant.png" />
                </picture>
            </div>
            <p
                v-if="isCromi() && directionSlug !== 'cabin'"
                class="mt-10 text-center text-16/148 font-medium text-accent-100">
                В&nbsp;среднем на&nbsp;ремонт поступает &lt;0,3% всех устройств
                в&nbsp;год
            </p>
        </div>
    </section>
</template>

<script setup lang="ts">
import { useIntersectionObserver } from '@vueuse/core'
import type {
    TCromiRentDirectionSlug,
    TDirectionSlug,
} from '~/types/global.types'

import AtomTitle from '~/components/atom/atom-title/AtomTitle.vue'
import type { IWarrantyItemsByDirection } from '~/components/particles/warranty-section/warranty-section.types'

const props = defineProps({
    directionSlug: {
        type: String as PropType<TDirectionSlug | TCromiRentDirectionSlug>,
        required: true,
    },
})

//todo: вынести в родительский компонент, или в отедельные константы и подтягивать через функицию чтобы не грузить лишние объекты
const WARRANTY_ITEMS_BY_DIRECTION: IWarrantyItemsByDirection = {
    radioguide: [
        {
            video: '/assets/video/fix.mp4',
            title: 'Гарантийный ремонт в&nbsp;нашем&nbsp;СЦ',
            description:
                'Гарантия на&nbsp;все радиогиды&nbsp;&mdash; 1&nbsp;год. Бесплатно устраним любые неполадки или заменим устройство.',
        },
        {
            video: '/assets/video/tech-support.mp4',
            title: 'Техническая поддержка 24/7',
            description:
                'Если возникли вопросы во&nbsp;время настройки или радиогид вышел из&nbsp;строя, звоните, мы&nbsp;поможем. Скорость ответа эксперта&nbsp;&mdash; 10-15&nbsp;минут.',
        },
    ],
    audioguide: [
        {
            video: '/assets/video/fix.mp4',
            title: 'Гарантийный ремонт в&nbsp;нашем&nbsp;СЦ',
            description:
                'Гарантия на&nbsp;все аудиогиды&nbsp;&mdash; 3&nbsp;года. Бесплатно устраним любые неполадки или заменим устройство.',
        },
        {
            video: '/assets/video/tech-support.mp4',
            title: 'Техническая поддержка 24/7',
            description:
                'Если возникли вопросы во&nbsp;время настройки или аудиогид вышел из&nbsp;строя, звоните, мы&nbsp;поможем. Скорость ответа эксперта&nbsp;&mdash; 10-15&nbsp;минут.',
        },
    ],
    cabin: [
        {
            video: '/assets/video/fix.mp4',
            title: 'Гарантийный ремонт в&nbsp;нашем&nbsp;СЦ',
            description:
                'Гарантия на&nbsp;все электронные компоненты кабин составляет 1&nbsp;год. Бесплатно устраним любые неполадки или заменим элемент.',
        },
        {
            video: '/assets/video/tech-support.mp4',
            title: 'Техническая поддержка 24/7',
            description:
                'Если возникли вопросы во&nbsp;время сборки кабины, звоните, мы&nbsp;поможем. Скорость ответа эксперта&nbsp;&mdash; 10-15 минут',
        },
    ],
    sinhroperevod: [
        {
            video: '/assets/video/fix.mp4',
            title: 'Гарантийный ремонт в&nbsp;нашем&nbsp;СЦ',
            description:
                'Гарантия на&nbsp;всё оборудование SPBAUDIO&nbsp;&mdash; 3&nbsp;года, Reinvox&nbsp;&mdash; 1&nbsp;год. Бесплатно устраним любые неполадки или заменим устройство.',
        },
        {
            video: '/assets/video/tech-support.mp4',
            title: 'Техническая поддержка 24/7',
            description:
                'Если возникли вопросы во&nbsp;время настройки&nbsp;&mdash; звоните, мы&nbsp;поможем. Скорость ответа эксперта&nbsp;&mdash; 10-15&nbsp;минут.',
        },
    ],
    'conference-system': [
        {
            video: '/assets/video/fix.mp4',
            title: 'Гарантийный ремонт в&nbsp;нашем&nbsp;СЦ',
            description:
                'Гарантия на&nbsp;все конференц-системы&nbsp;&mdash; 1&nbsp;год. Бесплатно устраним любые неполадки или заменим устройство.',
        },
        {
            video: '/assets/video/tech-support.mp4',
            title: 'Техническая поддержка 24/7',
            description:
                'Если возникли вопросы во&nbsp;время настройки или микрофон вышел из&nbsp;строя, звоните, мы&nbsp;поможем. Скорость ответа эксперта&nbsp;&mdash; 10-15 минут',
        },
    ],
    dostupsreda: [
        {
            video: '/assets/video/fix.mp4',
            title: 'Гарантийный ремонт в&nbsp;нашем&nbsp;СЦ',
            description:
                'Гарантия на&nbsp;всё оборудование Reinvox&nbsp;&mdash; 1&nbsp;год. Бесплатно устраним любые неполадки или заменим устройство.',
        },
        {
            video: '/assets/video/tech-support.mp4',
            title: 'Техническая поддержка 24/7',
            description:
                'Если возникли вопросы во&nbsp;время настройки или оборудование вышло из&nbsp;строя, звоните, мы&nbsp;поможем. Скорость ответа эксперта&nbsp;&mdash; 10-15&nbsp;минут.',
        },
    ],
    'arenda-radioguides': [
        {
            video: '/assets/video/fix.mp4',
            title: 'Собственный сервис центр',
            description:
                'Свой сервис центр позволяет поддерживать весь парк оборудования в&nbsp;идеальном состоянии&nbsp;&mdash; проводим профилактику, чекапы, тестируем, ремонтируем. Любим своё дело.',
        },
        {
            video: '/assets/video/tech-support.mp4',
            title: 'Штат технических экспертов онлайн',
            description:
                'Все этапы подготовки и&nbsp;реализации вашего мероприятия находятся под пристальным контролем технического директора и&nbsp;инженеров с&nbsp;опытом 15&nbsp;лет',
        },
    ],
    'arenda-audioguides': [
        {
            video: '/assets/video/fix.mp4',
            title: 'Собственный сервис центр',
            description:
                'Свой сервис центр позволяет поддерживать весь парк оборудования в&nbsp;идеальном состоянии&nbsp;&mdash; проводим профилактику, чекапы, тестируем, ремонтируем. Любим своё дело.',
        },
        {
            video: '/assets/video/tech-support.mp4',
            title: 'Штат технических экспертов онлайн',
            description:
                'Все этапы подготовки и&nbsp;реализации вашего мероприятия находятся под пристальным контролем технического директора и&nbsp;инженеров с&nbsp;опытом 15&nbsp;лет',
        },
    ],
    'arenda-video': [
        {
            video: '/assets/video/fix.mp4',
            title: 'Собственный сервис центр',
            description:
                'Свой сервис центр позволяет поддерживать весь парк оборудования в&nbsp;идеальном состоянии&nbsp;&mdash; проводим профилактику, чекапы, тестируем, ремонтируем. Любим своё дело.',
        },
        {
            video: '/assets/video/tech-support.mp4',
            title: 'Штат технических экспертов онлайн',
            description:
                'Все этапы подготовки и&nbsp;реализации вашего мероприятия находятся под пристальным контролем технического директора и&nbsp;инженеров с&nbsp;опытом 15&nbsp;лет',
        },
    ],
    'arenda-zvuka': [
        {
            video: '/assets/video/fix.mp4',
            title: 'Собственный сервис центр',
            description:
                'Свой сервис центр позволяет поддерживать весь парк оборудования в&nbsp;идеальном состоянии&nbsp;&mdash; проводим профилактику, чекапы, тестируем, ремонтируем. Любим своё дело.',
        },
        {
            video: '/assets/video/tech-support.mp4',
            title: 'Штат технических экспертов онлайн',
            description:
                'Все этапы подготовки и&nbsp;реализации вашего мероприятия находятся под пристальным контролем технического директора и&nbsp;инженеров с&nbsp;опытом 15&nbsp;лет',
        },
    ],
    'arenda-oborudovanie-dlya-sinhronnogo-perevoda': [
        {
            video: '/assets/video/fix.mp4',
            title: 'Собственный сервис центр',
            description:
                'Свой сервис центр позволяет поддерживать весь парк оборудования в&nbsp;идеальном состоянии&nbsp;&mdash; проводим профилактику, чекапы, тестируем, ремонтируем. Любим своё дело.',
        },
        {
            video: '/assets/video/tech-support.mp4',
            title: 'Штат технических экспертов онлайн',
            description:
                'Все этапы подготовки и&nbsp;реализации вашего мероприятия находятся под пристальным контролем технического директора и&nbsp;инженеров с&nbsp;опытом 15&nbsp;лет',
        },
    ],
    'arenda-konferents-system': [
        {
            video: '/assets/video/fix.mp4',
            title: 'Собственный сервис центр',
            description:
                'Свой сервис центр позволяет поддерживать весь парк оборудования в&nbsp;идеальном состоянии&nbsp;&mdash; проводим профилактику, чекапы, тестируем, ремонтируем. Любим своё дело.',
        },
        {
            video: '/assets/video/tech-support.mp4',
            title: 'Штат технических экспертов онлайн',
            description:
                'Все этапы подготовки и&nbsp;реализации вашего мероприятия находятся под пристальным контролем технического директора и&nbsp;инженеров с&nbsp;опытом 15&nbsp;лет',
        },
    ],
}

const videosContainer = ref<HTMLElement | null>(null)
const videoCards = ref<HTMLElement[]>([])

const videos = computed<HTMLVideoElement[]>(() => {
    if (!videosContainer.value) return []

    return Array.from(videosContainer.value.querySelectorAll('video'))
})

useIntersectionObserver(videosContainer, ([entry]) => {
    if (entry.isIntersecting) {
        videos.value
            .find((video: HTMLVideoElement) => {
                return getVideoOrder(video) === 0
            })
            ?.play()
    } else {
        videos.value.forEach((video: HTMLVideoElement) => {
            video.pause()
            video.load()
        })
    }
})

/**
 * Получает порядок видео
 * @param { HTMLVideoElement } video - Видео элемент
 * @returns { number } - Порядок, в котором стоит видео
 */
function getVideoOrder(video: HTMLVideoElement): number {
    return Number(video.getAttribute('data-order'))
}

/**
 * Отслеживает окончание видео
 *
 * При окончании видео включает следующее
 *
 * @param { Event } event - Эвент окончания видео
 */
function handleVideoEnded(event: Event): void {
    if (!(event.target instanceof Element) || !event.target) return

    if (getVideoOrder(event.target as HTMLVideoElement) === 0) {
        videos.value
            .find((video: HTMLVideoElement) => {
                return getVideoOrder(video) === 1
            })
            ?.play()
    } else {
        videos.value
            .find((video: HTMLVideoElement) => {
                return getVideoOrder(video) === 0
            })
            ?.play()
    }
}
</script>
